import { useCallback, useEffect, useState } from 'react';

export type ExportConfigurationIds = number[];

export type Reviewer =
    | {
          securityIdentityType: 'USER';
          identityId: string;
          bynderUserId: string;
          firstName: string;
          lastName: string;
          email: string;
      }
    | {
          securityIdentityType: 'PROFILE';
          profileId: string;
          profileName: string;
          customerId: string;
      }
    | {
          securityIdentityType: 'GROUP';
          groupId: string;
          groupName: string;
          customerId: string;
      };

type ExportIntegration = {
    channelType: string;
    enabled: boolean;
};

type UseVirtualPropertiesProps = {
    integrations?: any[];
    approvalEnabled?: boolean;
    reviewers?: Reviewer[];
    exportConfigurationIds?: ExportConfigurationIds;
};

const useVirtualProperties = ({
    integrations,
    approvalEnabled: approvalEnabledDefaultValue,
    reviewers: reviewersDefaultValue = [],
    exportConfigurationIds: exportConfigurationIdsDefaultValue = [],
}: UseVirtualPropertiesProps) => {
    const [exportIntegrations, setExportIntegrations] = useState<{ [key: string]: ExportIntegration }>({});
    const [approvalEnabled, setApprovalEnabled] = useState(approvalEnabledDefaultValue || false);
    const [reviewers, setReviewers] = useState<Reviewer[]>(reviewersDefaultValue || []);
    const [exportConfigurationIds, setExportConfigurationIdsState] = useState<ExportConfigurationIds>(
        exportConfigurationIdsDefaultValue,
    );
    const [globalsTab, setGlobalsTab] = useState<'Page' | 'Design'>('Page');
    const [modalActive, setModalActive] = useState(false);

    const extractInitialData = () => {
        const tempIntegrations = {};
        (integrations || []).forEach((item: any) => {
            tempIntegrations[item.channelType] = item;
        });
        setExportIntegrations(tempIntegrations);
    };

    const toggleExportIntegration = (exportName) => {
        if (exportIntegrations[exportName]) {
            exportIntegrations[exportName].enabled = !exportIntegrations[exportName].enabled;
        } else {
            exportIntegrations[exportName] = {
                channelType: exportName,
                enabled: true,
            };
        }
    };

    const changeExportValue = (newValue) => {
        if (newValue === 'DOWNLOAD_BYNDER') {
            setExportIntegrations({
                ...exportIntegrations,
                DOWNLOAD: {
                    channelType: 'DOWNLOAD',
                    enabled: true,
                },
                BYNDER: {
                    channelType: 'BYNDER',
                    enabled: true,
                },
            });
        } else {
            setExportIntegrations({
                ...exportIntegrations,
                DOWNLOAD: {
                    channelType: 'DOWNLOAD',
                    enabled: false,
                },
                BYNDER: {
                    channelType: 'BYNDER',
                    enabled: false,
                },
                [newValue]: {
                    channelType: newValue,
                    enabled: true,
                },
            });
        }
    };

    const setExportConfigurationIds = useCallback((nextExportConfigurationIds: ExportConfigurationIds) => {
        setExportConfigurationIdsState(nextExportConfigurationIds);
    }, []);

    const addReviewer = useCallback<(reviewer: Reviewer) => void>((reviewer) => {
        setReviewers((prevReviewers) => [...prevReviewers, reviewer]);
    }, []);

    const removeReviewer = useCallback<(reviewer: Reviewer) => void>((reviewer) => {
        setReviewers((prevReviewers) =>
            prevReviewers.filter((item) => {
                if (reviewer.securityIdentityType === 'USER' && item.securityIdentityType === 'USER') {
                    return item.bynderUserId !== reviewer.bynderUserId;
                }

                if (reviewer.securityIdentityType === 'PROFILE' && item.securityIdentityType === 'PROFILE') {
                    return item.profileId !== reviewer.profileId || item.customerId !== reviewer.customerId;
                }

                if (reviewer.securityIdentityType === 'GROUP' && item.securityIdentityType === 'GROUP') {
                    return item.groupId !== reviewer.groupId;
                }

                return true;
            }),
        );
    }, []);

    useEffect(() => {
        extractInitialData();
    }, []);

    return {
        exportIntegrations,
        toggleExportIntegration,
        changeExportValue,
        approvalEnabled,
        setApprovalEnabled,
        reviewers,
        addReviewer,
        removeReviewer,
        exportConfigurationIds,
        setExportConfigurationIds,
        globalsTab,
        setGlobalsTab,
        modalActive,
        setModalActive,
    };
};

export default useVirtualProperties;
